import { useAppQuery, InlineError, useTokenData } from "@phonero/common-ux"
import css from "./SelectSimcard.module.scss"
import { PuxInput, PuxSimcard, PuxSpinner, PuxText, useTranslation } from "@phonero/pux-react"
import { SimCardType2 } from "@phonero/common-graphql/types"
import { formatTelephoneNumber } from "@phonero/pux-core"
import classNames from "classnames"
import { getStateColour, ValidateSimCardDocument } from "@phonero/common-ux"
export type SelectSimCardData = {
  simType: SimCardType2
  simNumber: string
  simNumberStatus: "Pending" | "Valid" | "Invalid"
  iccIdFullLength: string | undefined // full icc number (length 19 og 20 characters) and verified from API.
}
export interface ISelectSimCardProps {
  simData: SelectSimCardData | undefined
  assignSimData: (simData: SelectSimCardData) => void
  dark?: boolean
  isDuplicateSimNumber?: boolean
  externalValidation?: (iccid: string) => boolean
  readonly?: boolean
  index?: number
}
function isValidSimNumber(number: string) {
  return number?.length === 12 && /^[0-9]{0,12}$/.test(number)
}
type SimCardType = string | "SUBSCRIPTION" | "TWIN" | "DATACARD" | "EMBEDDED" | "" | undefined
interface ISimCardProps {
  type?: SimCardType
  // shadow?: boolean
  msisdn?: string | null
  // activationDate?: Date | null
  // deactivationDate?: Date | null
  pin1?: string | null
  pin2?: string | null
  puk1?: string | null
  puk2?: string | null
  // iccid?: string | null
  status: string | "ACTIVE" | "AVAILIBLE" | "DEACTIVE" | "ERROR" | "ACTIVATING" | "DEACTIVATING" | "ORDERED"
  selectIccidStatus?: "VALID" | "INVALID" | "SEARCHING" | "NONE" | "ERROR"
}
export const SelectSimCard = ({
  simData,
  assignSimData,
  isDuplicateSimNumber,
  externalValidation,
  dark,
  readonly,
  index,
}: ISelectSimCardProps) => {
  const { t } = useTranslation()
  const { simNumber, simType } = simData || {}
  const { customerId = "" } = useTokenData()

  // Iccid is 19 or 20 digits long, but only the 12 last digits are displayed on the SIM letter.
  // Use the 12 last digits when searching / displaying SimNumber to user.
  const [_simNumber, _setSimNumber] = useState(simNumber?.substring(simNumber.length - 12))
  const [simCardProps, setSimCardProps] = useState<ISimCardProps>({
    ...defaultSimCardProps(simType),
    selectIccidStatus: _simNumber?.length === 12 ? "SEARCHING" : "NONE",
  })
  const { loading, error } = useAppQuery(ValidateSimCardDocument, {
    fetchPolicy: "network-only",
    variables: { iccid: _simNumber, customerId: customerId },
    skip: !_simNumber || !customerId || !isValidSimNumber(_simNumber),
    onCompleted: (data) => {
      const warehouseSimCardByIccid = data?.warehouseSimCardByIccid
      if (!_simNumber || !data || !simData) {
        setSimCardProps((d) => ({
          ...d,
          selectIccidStatus: "ERROR" as any,
        }))
        return
      }
      if (externalValidation && !externalValidation(_simNumber)) {
        setSimCardProps((d) => ({
          ...d,
          selectIccidStatus: "INVALID",
        }))
        assignSimData({
          ...simData,
          simNumber: _simNumber,
          simNumberStatus: "Invalid",
          iccIdFullLength: undefined,
        })
        return
      }
      if (!warehouseSimCardByIccid) {
        assignSimData({
          ...simData,
          simNumber: _simNumber,
          simNumberStatus: "Invalid",
          iccIdFullLength: undefined,
        })
        setSimCardProps((d) => ({
          ...d,
          selectIccidStatus: "INVALID",
        }))
        return
      }
      if (!warehouseSimCardByIccid.iccid?.endsWith(_simNumber)) {
        assignSimData({
          ...simData,
          simNumber: _simNumber,
          simNumberStatus: "Pending",
          iccIdFullLength: undefined,
        })
        setSimCardProps((d) => ({
          ...d,
          selectIccidStatus: "SEARCHING",
        }))
        return
      }
      if (!warehouseSimCardByIccid.activationDate && !warehouseSimCardByIccid.deactivationDate) {
        assignSimData({
          ...simData,
          simNumber: _simNumber,
          simNumberStatus: "Valid",
          iccIdFullLength: warehouseSimCardByIccid.iccid,
        })
        setSimCardProps((d) => ({
          ...d,
          selectIccidStatus: "VALID",
          pin1: "****",
          puk1: "********",
        }))
        return
      }
      assignSimData({
        ...simData,
        simNumber: _simNumber,
        simNumberStatus: "Invalid",
        iccIdFullLength: undefined,
      })
      setSimCardProps((d) => ({
        ...d,
        selectIccidStatus: "INVALID",
      }))
    },
  })
  if (!simData) return null
  if (!simCardProps) setSimCardProps(defaultSimCardProps(simType)) // Set default values
  const setSimNumber = (iccid: string) => {
    _setSimNumber(iccid)
    if (!iccid || !isValidSimNumber(iccid)) {
      setSimCardProps((d) => ({
        ...d,
        pin1: "",
        puk1: "",
        selectIccidStatus: "NONE",
      }))
      assignSimData({
        ...simData,
        simNumber: iccid,
        simNumberStatus: "Pending",
      })
      return
    }
    assignSimData({
      ...simData,
      simNumber: iccid,
      simNumberStatus: "Pending",
    })
    setSimCardProps((d) => ({
      ...d,
      iccid,
      selectIccidStatus: "SEARCHING",
    }))
  }
  return (
    <>
      <div className={css.simCardContainer} style={{ alignContent: "center", paddingTop: "2.5rem" }}>
        <div className={css.errorContainer}>
          <PuxSimcard color="light">
            <PuxText slot="name">
              {t("SimCard.Type", { context: simCardProps?.type })} {index}
            </PuxText>
            <PuxText slot="msisdn">
              {simCardProps?.msisdn && formatTelephoneNumber(simCardProps?.msisdn).formatted}
            </PuxText>
            {!!simCardProps?.pin1 && <PuxText slot="pin1">{simCardProps?.pin1}</PuxText>}
            {!!simCardProps.puk1 && <PuxText slot="puk1">{simCardProps?.puk1}</PuxText>}
            <PuxText slot="iccid"></PuxText>
            <PuxText slot="chip" color={getStateColour(simCardProps?.selectIccidStatus)}>
              {t("IccidStatus", { context: simCardProps.selectIccidStatus })}
            </PuxText>
            <div className={css.simInputContainer} slot="numberInput">
              {!readonly && (
                <div className={css.status}>
                  <PuxInput
                    type="text"
                    name="iccid"
                    className={classNames(
                      (simCardProps.selectIccidStatus === "INVALID" || simCardProps.selectIccidStatus === "ERROR") &&
                        "invalid"
                    )}
                    required={true}
                    value={_simNumber || ""}
                    placeholder={t("SelectSimCard.IccIdNumbers")}
                    onPuxChange={(e) => {
                      setSimNumber(String(e.target.value))
                    }}
                    /*onPuxBlur={(e) => {
                    if (!isValidSimNumber((e.target as HTMLInputElement).value)) {
                      setSimData({
                        ...simData,
                        simNumber: "",
                        // dette skriver ut sim kortet ikke tilgjengelig. burde splittes i Invalid og Unavailable
                        simNumberStatus: "Invalid",
                      })
                    }
                  }}*/
                    maxlength={12}
                    style={{
                      height: "3rem",
                      width: "140px",
                      marginTop: "-0.7rem",
                    }}
                  />
                  <PuxSpinner color="dark" className={classNames(loading && css.active)} />
                </div>
              )}
              {readonly && <PuxText>{_simNumber}</PuxText>}
            </div>
          </PuxSimcard>
          <InlineError code="ValidateSimCard" error={error} />
          {simData?.simNumberStatus === "Invalid" && (
            <div data-pux-dark={dark} className={css.simInputInvalid} style={{}}>
              {t("SelectSimCard.ErrorInvalidNumber")}.
            </div>
          )}
          {isDuplicateSimNumber && (
            <div data-pux-dark={dark} className={css.simInputInvalid}>
              {t("SelectSimCard.ErrorDuplicateNumber")}.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
const defaultSimCardProps = (
  sim: undefined | null | string | { simType: undefined | null | string }
): ISimCardProps => {
  const simType = typeof sim === "object" ? sim?.simType : sim
  return {
    msisdn: "",
    pin1: "",
    pin2: "",
    puk1: "",
    puk2: "",
    type:
      simType === "SUBSCRIPTION"
        ? "SUBSCRIPTION"
        : simType === "TWIN"
        ? "TWIN"
        : simType === "DATACARD"
        ? "DATACARD"
        : undefined,
    status: "SEARCH",
    selectIccidStatus: "NONE",
  }
}
